import { v } from "convex/values";

/**
 * Single source of truth for the domain's string-literal unions.
 *
 * Each enum is declared once as a `const` tuple (giving us the TS union type)
 * alongside an explicit Convex validator. The two are kept adjacent so they
 * cannot drift. Pure domain code (src/domain) imports the *types* from here;
 * Convex schema + functions import the *validators*.
 */

// --- Australian jurisdictions -------------------------------------------------
export const JURISDICTIONS = [
  "COM", // Commonwealth
  "WA",
  "NSW",
  "QLD",
  "VIC",
  "SA",
  "NT",
  "ACT",
  "TAS",
] as const;
export type Jurisdiction = (typeof JURISDICTIONS)[number];
export const jurisdiction = v.union(
  v.literal("COM"),
  v.literal("WA"),
  v.literal("NSW"),
  v.literal("QLD"),
  v.literal("VIC"),
  v.literal("SA"),
  v.literal("NT"),
  v.literal("ACT"),
  v.literal("TAS"),
);

export const JURISDICTION_LABELS: Record<Jurisdiction, string> = {
  COM: "Commonwealth",
  WA: "Western Australia",
  NSW: "New South Wales",
  QLD: "Queensland",
  VIC: "Victoria",
  SA: "South Australia",
  NT: "Northern Territory",
  ACT: "Australian Capital Territory",
  TAS: "Tasmania",
};

// --- Legislation --------------------------------------------------------------
export const LEGISLATION_KINDS = ["act", "regulation"] as const;
export type LegislationKind = (typeof LEGISLATION_KINDS)[number];
export const legislationKind = v.union(v.literal("act"), v.literal("regulation"));

export type LegislationCategory = "legislative" | "otherRequirement";
export const legislationCategory = v.union(
  v.literal("legislative"),
  v.literal("otherRequirement"),
);

export type LegislationStatus = "active" | "inactive";
export const legislationStatus = v.union(v.literal("active"), v.literal("inactive"));

// --- Section tree -------------------------------------------------------------
// Part > Division > Subdivision > Section (the leaf carrying content).
export type NodeType = "part" | "division" | "subdivision" | "section";
export const nodeType = v.union(
  v.literal("part"),
  v.literal("division"),
  v.literal("subdivision"),
  v.literal("section"),
);

// --- Subscriptions ------------------------------------------------------------
// Per-legislation tier. "premium" delivers richer / more alerts for that law.
export const SUBSCRIPTION_TYPES = ["standard", "premium"] as const;
export type SubscriptionType = (typeof SUBSCRIPTION_TYPES)[number];
export const subscriptionType = v.union(v.literal("standard"), v.literal("premium"));

export type SubscriptionSource = "bundle" | "manual";
export const subscriptionSource = v.union(v.literal("bundle"), v.literal("manual"));

// --- Company policies / impact review ----------------------------------------
export type PolicyStatus = "active" | "draft" | "archived";
export const policyStatus = v.union(
  v.literal("active"),
  v.literal("draft"),
  v.literal("archived"),
);

export type PolicyImpactLevel = "high" | "medium" | "low";
export const policyImpactLevel = v.union(
  v.literal("high"),
  v.literal("medium"),
  v.literal("low"),
);

export type PolicyReviewStatus = "pending" | "inReview" | "updated" | "notAffected";
export const policyReviewStatus = v.union(
  v.literal("pending"),
  v.literal("inReview"),
  v.literal("updated"),
  v.literal("notAffected"),
);

export const POLICY_OBLIGATION_COVERAGE_STATUSES = [
  "covered",
  "partial",
  "missing",
  "unclear",
  "needsLegalReview",
] as const;
export type PolicyObligationCoverageStatus = (typeof POLICY_OBLIGATION_COVERAGE_STATUSES)[number];
export const policyObligationCoverageStatus = v.union(
  v.literal("covered"),
  v.literal("partial"),
  v.literal("missing"),
  v.literal("unclear"),
  v.literal("needsLegalReview"),
);

export const POLICY_MAPPING_CONFIDENCES = ["high", "medium", "low"] as const;
export type PolicyMappingConfidence = (typeof POLICY_MAPPING_CONFIDENCES)[number];
export const policyMappingConfidence = v.union(
  v.literal("high"),
  v.literal("medium"),
  v.literal("low"),
);

export const POLICY_MAPPING_COMPLIANCE_STATUSES = [
  "complies",
  "doesNotComply",
  "partiallyComplies",
  "unclear",
  "needsLegalReview",
  "notMapped",
] as const;
export type PolicyMappingComplianceStatus = (typeof POLICY_MAPPING_COMPLIANCE_STATUSES)[number];
export const policyMappingComplianceStatus = v.union(
  v.literal("complies"),
  v.literal("doesNotComply"),
  v.literal("partiallyComplies"),
  v.literal("unclear"),
  v.literal("needsLegalReview"),
  v.literal("notMapped"),
);

export const POLICY_MAPPING_VERIFICATION_STATUSES = [
  "aiUnverified",
  "humanVerified",
  "rejected",
] as const;
export type PolicyMappingVerificationStatus = (typeof POLICY_MAPPING_VERIFICATION_STATUSES)[number];
export const policyMappingVerificationStatus = v.union(
  v.literal("aiUnverified"),
  v.literal("humanVerified"),
  v.literal("rejected"),
);

export const POLICY_MAPPING_SOURCES = ["ai", "human"] as const;
export type PolicyMappingSource = (typeof POLICY_MAPPING_SOURCES)[number];
export const policyMappingSource = v.union(v.literal("ai"), v.literal("human"));

// --- Business scopes ---------------------------------------------------------
export const BUSINESS_SCOPE_KINDS = [
  "mine",
  "site",
  "businessUnit",
  "subsidiary",
  "projectArea",
  "other",
] as const;
export type BusinessScopeKind = (typeof BUSINESS_SCOPE_KINDS)[number];
export const businessScopeKind = v.union(
  v.literal("mine"),
  v.literal("site"),
  v.literal("businessUnit"),
  v.literal("subsidiary"),
  v.literal("projectArea"),
  v.literal("other"),
);

// --- Compliance projects -----------------------------------------------------
export type ComplianceProjectStatus =
  | "draft"
  | "active"
  | "atRisk"
  | "readyForApproval"
  | "complete";
export const complianceProjectStatus = v.union(
  v.literal("draft"),
  v.literal("active"),
  v.literal("atRisk"),
  v.literal("readyForApproval"),
  v.literal("complete"),
);

export const COMPLIANCE_PROJECT_TRIGGERS = [
  "manual",
  "providerAlert",
  "legislativeChange",
  "standardsChange",
  "periodicReview",
] as const;
export type ComplianceProjectTrigger = (typeof COMPLIANCE_PROJECT_TRIGGERS)[number];
export const complianceProjectTrigger = v.union(
  v.literal("manual"),
  v.literal("providerAlert"),
  v.literal("legislativeChange"),
  v.literal("standardsChange"),
  v.literal("periodicReview"),
);

export type ComplianceTaskStatus =
  | "unassigned"
  | "toReview"
  | "inProgress"
  | "readyForApproval"
  | "approved"
  | "noChangeRequired"
  | "blocked";
export const complianceTaskStatus = v.union(
  v.literal("unassigned"),
  v.literal("toReview"),
  v.literal("inProgress"),
  v.literal("readyForApproval"),
  v.literal("approved"),
  v.literal("noChangeRequired"),
  v.literal("blocked"),
);

export type ComplianceTaskDecision =
  | "policyUpdated"
  | "noChangeRequired"
  | "deferred"
  | "blocked";
export const complianceTaskDecision = v.union(
  v.literal("policyUpdated"),
  v.literal("noChangeRequired"),
  v.literal("deferred"),
  v.literal("blocked"),
);

export type ComplianceActivityKind =
  | "created"
  | "statusChanged"
  | "assigned"
  | "commented"
  | "taskAdded"
  | "taskUpdated"
  | "completed";
export const complianceActivityKind = v.union(
  v.literal("created"),
  v.literal("statusChanged"),
  v.literal("assigned"),
  v.literal("commented"),
  v.literal("taskAdded"),
  v.literal("taskUpdated"),
  v.literal("completed"),
);

// --- Change log ---------------------------------------------------------------
export const CHANGE_TYPES = ["amended", "insertion", "repeal"] as const;
export type ChangeType = (typeof CHANGE_TYPES)[number];
export const changeType = v.union(
  v.literal("amended"),
  v.literal("insertion"),
  v.literal("repeal"),
);

export const ALERT_KINDS = ["principal", "amending", "bill", "proclamation", "manual"] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];
export const alertKind = v.union(
  v.literal("principal"),
  v.literal("amending"),
  v.literal("bill"),
  v.literal("proclamation"),
  v.literal("manual"),
);

export const ALERT_STATUSES = ["new", "inProgress", "actioned", "deferred"] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];
export const alertStatus = v.union(
  v.literal("new"),
  v.literal("inProgress"),
  v.literal("actioned"),
  v.literal("deferred"),
);

// --- Users / roles ------------------------------------------------------------
// internalAdmin + markupAdmin are Lawstream staff; the two client roles are
// the customer-facing ones.
export const CLIENT_ROLES = ["clientAdmin", "clientReadOnly"] as const;
export type ClientRole = (typeof CLIENT_ROLES)[number];
export const clientRole = v.union(v.literal("clientAdmin"), v.literal("clientReadOnly"));

export const ROLES = [
  "internalAdmin",
  "markupAdmin",
  "clientAdmin",
  "clientReadOnly",
] as const;
export type Role = (typeof ROLES)[number];
export const role = v.union(
  v.literal("internalAdmin"),
  v.literal("markupAdmin"),
  v.literal("clientAdmin"),
  v.literal("clientReadOnly"),
);

// --- Company billing status ---------------------------------------------------
export type CompanyStatus = "trialing" | "active" | "pastDue" | "canceled";
export const companyStatus = v.union(
  v.literal("trialing"),
  v.literal("active"),
  v.literal("pastDue"),
  v.literal("canceled"),
);

// --- Bundle theme (the "Core ___" packs) -------------------------------------
export type BundleTheme = "safety" | "business" | "environmental";
export const bundleTheme = v.union(
  v.literal("safety"),
  v.literal("business"),
  v.literal("environmental"),
);

// --- Billing interval ---------------------------------------------------------
export type BillingInterval = "month" | "year";
export const billingInterval = v.union(v.literal("month"), v.literal("year"));

// --- Source imports ----------------------------------------------------------
export type SourceProvider =
  | "frl"
  | "nsw"
  | "qld"
  | "wa"
  | "vic"
  | "sa"
  | "nt"
  | "act"
  | "tas"
  | "manual";
export const sourceProvider = v.union(
  v.literal("frl"),
  v.literal("nsw"),
  v.literal("qld"),
  v.literal("wa"),
  v.literal("vic"),
  v.literal("sa"),
  v.literal("nt"),
  v.literal("act"),
  v.literal("tas"),
  v.literal("manual"),
);

export type SourceFormat =
  | "json"
  | "xml"
  | "xhtml"
  | "html"
  | "epub"
  | "pdf"
  | "docx"
  | "text";
export const sourceFormat = v.union(
  v.literal("json"),
  v.literal("xml"),
  v.literal("xhtml"),
  v.literal("html"),
  v.literal("epub"),
  v.literal("pdf"),
  v.literal("docx"),
  v.literal("text"),
);

export type ImportRunStatus = "started" | "published" | "failed";
export const importRunStatus = v.union(
  v.literal("started"),
  v.literal("published"),
  v.literal("failed"),
);
